import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import {
  Box,
  Chip,
  Divider,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';

const useStyles = makeStyles(theme => ({
  label: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    width: '40%',
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  value: {
    wordBreak: 'break-all',
    fontSize: '0.85rem',
  },
  emptyValue: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
    fontSize: '0.85rem',
  },
  provisionedChip: {
    backgroundColor: theme.palette.success?.main ?? '#1db954',
    color: '#fff',
    height: 20,
    fontSize: '0.7rem',
  },
  pendingChip: {
    height: 20,
    fontSize: '0.7rem',
  },
  sectionHeader: {
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.disabled,
    padding: theme.spacing(1, 2, 0.5),
  },
}));

interface InfoRowProps {
  label: string;
  value?: string | boolean | number;
  emptyText?: string;
}

function InfoRow({ label, value, emptyText = '—' }: InfoRowProps) {
  const classes = useStyles();
  const isEmpty = value === undefined || value === null || value === '';
  return (
    <TableRow>
      <TableCell className={classes.label}>{label}</TableCell>
      <TableCell className={isEmpty ? classes.emptyValue : classes.value}>
        {isEmpty ? emptyText : String(value)}
      </TableCell>
    </TableRow>
  );
}

export function AzureProviderInfoCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const m = entity.metadata as Record<string, any>;

  const isProvisioned = !!m.workspaceUrl || !!m.workspaceId;
  const isDatabricks = m.envType === 'databricks';

  return (
    <InfoCard
      title="Provider Info"
      action={
        isProvisioned ? (
          <Chip
            icon={<CheckCircleOutlineIcon style={{ fontSize: 14 }} />}
            label="Provisioned"
            size="small"
            className={classes.provisionedChip}
          />
        ) : (
          <Chip
            icon={<HourglassEmptyIcon style={{ fontSize: 14 }} />}
            label="Pending provisioning"
            size="small"
            variant="outlined"
            className={classes.pendingChip}
          />
        )
      }
    >
      <Table size="small">
        <TableBody>
          <InfoRow label="Prefix" value={m.prefix} />
          <InfoRow label="Environment Type" value={m.envType} />
          <InfoRow label="Azure Subscription" value={m.azureSubscription} />
          <InfoRow label="Azure Region" value={m.azureRegion} />
          <InfoRow label="Resource Group" value={m.resourceGroup} />
          <InfoRow label="Service Connection" value={m.prefix ? `${m.prefix}-azure-service-connection` : undefined} />
        </TableBody>
      </Table>

      {isDatabricks && (
        <>
          <Divider />
          <Typography className={classes.sectionHeader}>Databricks Workspace</Typography>
          <Table size="small">
            <TableBody>
              <InfoRow label="Workspace Name" value={m.workspaceName} emptyText="Not yet provisioned" />
              <InfoRow label="Workspace URL" value={m.workspaceUrl} emptyText="Not yet provisioned" />
              <InfoRow label="Workspace ID" value={m.workspaceId} emptyText="Not yet provisioned" />
              <InfoRow label="SKU" value={m.databricksSku} />
              <InfoRow label="Unity Catalog Metastore" value={m.unityCatalogMetastoreId} emptyText="Not yet provisioned" />
            </TableBody>
          </Table>
        </>
      )}

      <Divider />
      <Typography className={classes.sectionHeader}>Audit</Typography>
      <Table size="small">
        <TableBody>
          <InfoRow label="Storage Account" value={m.auditStorageAccount} emptyText="Not yet provisioned" />
          <InfoRow label="Table" value={m.auditTable} emptyText="Not yet provisioned" />
        </TableBody>
      </Table>

      <Divider />
      <Typography className={classes.sectionHeader}>IaC</Typography>
      <Table size="small">
        <TableBody>
          <InfoRow label="Type" value={m.iacType} />
          <InfoRow label="State" value={m.componentState} />
          <InfoRow label="Git Provider" value={m.gitProvider} />
        </TableBody>
      </Table>

      {isDatabricks && (
        <>
          <Divider />
          <Typography className={classes.sectionHeader}>Cluster Defaults</Typography>
          <Table size="small">
            <TableBody>
              <InfoRow label="Runtime Version" value={m.defaultClusterVersion} />
              <InfoRow label="Worker Node Type" value={m.defaultNodeType} />
              <InfoRow label="Driver Node Type" value={m.defaultDriverNodeType} />
              <InfoRow
                label="Autoscaling"
                value={
                  m.enableAutoscaling
                    ? `Enabled (${m.minWorkers}–${m.maxWorkers} workers)`
                    : 'Disabled'
                }
              />
            </TableBody>
          </Table>
        </>
      )}

      <Box pb={1} />
    </InfoCard>
  );
}
